import React from 'react'
import { ShareListItemFragment, StorageItemFragment } from '@sketch/gql-types'

import { ConnectedDocumentItem } from '../DocumentItem'
import { useShareListItemDragDrop } from '../../hooks/useShareDragAndDrop'
import { DragWrapper } from './DraggableShareItem.styles'
import { Tooltip } from '@sketch/components'

type DraggableShareItemDropdown<S> = (item: S) => React.ReactNode

export interface DraggableShareItemProps<S> {
  item: S
  workspaceIdentifier: string
  userIdentifier?: string
  renderDropdown: DraggableShareItemDropdown<S>
  presentation: 'grid' | 'list'
  onClick?: React.MouseEventHandler<HTMLElement>
  draggable?: boolean
  droppable?: boolean
  onStorageItemsDropped?: (items: StorageItemFragment[]) => void
  selected?: boolean
  onSelectChange?: (selected: boolean) => void

  /* This property only applies to the grid presentation */
  showSelectionCheckbox?: boolean
}

export const DraggableShareItem = <S extends ShareListItemFragment>({
  item,
  workspaceIdentifier,
  userIdentifier,
  renderDropdown,
  presentation,
  onClick,
  draggable: passedDownDraggable,
  droppable,
  onStorageItemsDropped,
  selected,
  onSelectChange,
  showSelectionCheckbox,
}: DraggableShareItemProps<S>) => {
  // In the list view we don't want to be able to create nested projects by
  // dragging shares onto each other so we only use `draggable` and
  // `onDragStart` and ignore the `onDropped` handlers.
  const dragAndDropEvents = useShareListItemDragDrop({
    disabled: !passedDownDraggable || item.isOptimistic,
    shareId: item.identifier,
    onStorageItemsDropped,
  })

  const { isDraggedOver, isDraggedOverItself, isDroppableAreaEnabled } =
    dragAndDropEvents

  const documentItemsComponent = (
    <ConnectedDocumentItem
      key={item.identifier}
      workspaceId={workspaceIdentifier}
      share={item}
      renderDropdown={renderDropdown}
      userIdentifier={userIdentifier}
      presentation={presentation}
      onClick={onClick}
      dragAndDropEvents={dragAndDropEvents}
      droppable={droppable}
      selected={selected}
      onSelectChange={onSelectChange}
      showSelectionCheckbox={showSelectionCheckbox}
    />
  )

  const canCreateProjectByDragNDrop = Boolean(
    passedDownDraggable &&
      isDroppableAreaEnabled &&
      isDraggedOver &&
      !isDraggedOverItself &&
      onStorageItemsDropped
  )

  if (presentation === 'grid') {
    return (
      <DragWrapper
        data-testid="drag-wrapper"
        $isDraggedOver={canCreateProjectByDragNDrop}
        {...dragAndDropEvents}
      >
        <Tooltip
          disabled={!canCreateProjectByDragNDrop}
          content="Create a new folder"
          visible={canCreateProjectByDragNDrop}
          placement="top"
        >
          {documentItemsComponent}
        </Tooltip>
      </DragWrapper>
    )
  }

  return documentItemsComponent
}
